import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { PieChart } from 'echarts/charts';
import {
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { Table } from 'react-bootstrap';
import { getColor } from 'helpers/utils';
import useToken from 'hooks/useToken';

echarts.use([
  TitleComponent,
  TooltipComponent,
  PieChart,
  LegendComponent
]);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AA336A', '#6A33AA'];

const SOURCE_NAMES = {
    0: 'No asignado',
    1: 'WhatsApp',
    2: 'Messenger',
    3: 'Facebook',
    4: 'Tawk',
    5: 'Correo',
    6: 'Akasia Bot',
    7: 'Distribuidor'
};

const SourcePieChart = ({ height = 220 }) => {
    const [data, setData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const { token, setToken } = useToken();
    const abortRef = useRef(null);

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
        years.push(year);
    }

    useEffect(() => {
        if (!token) return;

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const formData = new URLSearchParams();
        formData.append('anio', selectedYear.toString());

        fetch('/api/marketing/ObtenerDistribucionFuentes', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': token ? 'Bearer ' + token : ''
            },
            body: formData.toString(),
            signal: controller.signal
        })
        .then((res) => {
            if (!res.ok) {
                if (res.status === 401) {
                    setToken(null);
                    location.href = '';
                }
                throw new Error('Error fetching source data');
            }
            return res.json();
        })
        .then((response) => {
            if (response.iResponseType === 1 && response.detalles) {
                const dataset = JSON.parse(response.detalles);
                const rows = dataset.table2 || [];
                
                const sourceData = rows.map(item => ({
                    name: SOURCE_NAMES[item.idSource] || `Fuente ${item.idSource}`,
                    value: parseInt(item.iTotalLeads || 0)
                })).filter(item => item.value > 0);

                setData(sourceData);
            } else {
                setData([]);
            }
        })
        .catch((err) => {
            if (err.name === 'AbortError') return;
            console.error('Error fetching source data:', err);
            setData([]);
        });

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [selectedYear, token]);

    const getOptions = () => ({
        tooltip: {
            trigger: 'item',
            padding: [7, 10],
            backgroundColor: getColor('gray-100'),
            borderColor: getColor('gray-300'),
            textStyle: { color: getColor('dark') },
            borderWidth: 1,
            formatter: '{b}: {c} ({d}%)'
        },
        legend: {
            orient: 'vertical',
            left: 'left',
            textStyle: { color: getColor('gray-700') }
        },
        series: [
            {
                name: 'Fuente',
                type: 'pie',
                radius: '70%',
                center: ['50%', '50%'],
                data: data.map((item, index) => ({
                    value: item.value,
                    name: item.name,
                    itemStyle: {
                        color: COLORS[index % COLORS.length]
                    }
                })),
                label: {
                    formatter: '{b} {d}%',
                    color: getColor('gray-700')
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }
        ]
    });

    const total = data.reduce((sum, item) => sum + item.value, 0);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Form.Select 
                    size="sm" 
                    value={selectedYear} 
                    style={{ width: 120 }} 
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </Form.Select>
            </div>

            <ReactEChartsCore 
                echarts={echarts} 
                option={getOptions()} 
                style={{ height: `${height}px` }}
            />
            <div className="mt-3">
                <Table striped bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Fuente</th>
                            <th>Cantidad</th>
                            <th>%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <span 
                                        style={{
                                            display: 'inline-block',
                                            width: 12,
                                            height: 12,
                                            backgroundColor: COLORS[index % COLORS.length],
                                            marginRight: 8,
                                            borderRadius: 2
                                        }}
                                    />
                                    {item.name}
                                </td>
                                <td>{item.value}</td>
                                <td>{total > 0 ? ((item.value / total) * 100).toFixed(1) : 0}%</td>
                            </tr>
                        ))}
                        {data.length > 0 && (
                            <tr style={{ fontWeight: 'bold' }}>
                                <td>Total</td>
                                <td>{total}</td>
                                <td>100%</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

SourcePieChart.propTypes = {
    height: PropTypes.number
};

SourcePieChart.defaultProps = {
    height: 220
};

export default SourcePieChart;
